// web/src/lib/useRoom.ts
import { useEffect, useRef, useState } from 'react'

export type RoomState = {
  round: number
  pot: number
  prize: number | null
  scores: { you: number; opp: number }
  yourRemaining: number[]
  lastReveal: null | {
    prize: number
    you: number
    opp: number
    outcome: string // "you" | "opp" | "tie"
    pot_after: number
    you_score_after: number
    opp_score_after: number
  }
  finished: boolean
  decisionDeadline: number | null
  decisionSeconds: number
  winner: 'you' | 'opp' | 'tie' | null
  endReason: string | null
  matchType?: string // 'pvp' or 'pve'
  opponentName?: string // opponent username for display
  opponentUsername?: string
  yourUsername?: string
  player1Username?: string
  player2Username?: string
  you_play?: number | null // current round player move
  opp_play?: number | null // current round opponent move
  ui_phase?: string // 'decide' | 'locked' | 'reveal' | 'interlude'
}

function normalizeState(raw: any, correctYourUsername?: string | null): RoomState {
  const round = typeof raw?.round === 'number' ? raw.round : 0
  const pot = typeof raw?.pot === 'number' ? raw.pot : 0
  const prize = typeof raw?.prize === 'number' ? raw.prize : null
  const scores = {
    you: typeof raw?.scores?.you === 'number' ? raw.scores.you : Number(raw?.youScore ?? 0),
    opp: typeof raw?.scores?.opp === 'number' ? raw.scores.opp : Number(raw?.oppScore ?? 0),
  }
  const yourRemaining = Array.isArray(raw?.yourRemaining)
    ? raw.yourRemaining
        .map((n: any) => Number(n))
        .filter((n: number) => Number.isFinite(n))
        .sort((a: number, b: number) => a - b)
    : []
  const lastReveal = raw?.lastReveal ?? null
  const finished = Boolean(raw?.finished)
  const deadline = typeof raw?.decisionDeadline === 'number' ? raw.decisionDeadline : null
  const seconds = typeof raw?.decisionSeconds === 'number' ? raw.decisionSeconds : 20
  const winner = raw?.winner === 'you' || raw?.winner === 'opp' || raw?.winner === 'tie' ? raw.winner : null
  const endReason = typeof raw?.endReason === 'string' ? raw.endReason : null
  const rawPlayer1 =
    raw?.player1Username ||
    raw?.player1_username ||
    (raw?.player1 && raw.player1.username) ||
    null
  const rawPlayer2 =
    raw?.player2Username ||
    raw?.player2_username ||
    (raw?.player2 && raw.player2.username) ||
    null
  
  // Use the correctYourUsername if provided to prevent perspective corruption
  let yourUsername = raw?.yourUsername || raw?.your_username || null
  let opponentUsername = raw?.opponentUsername || raw?.opponent_username || null
  
  // If we have a correctYourUsername, validate and fix the perspective if needed
  if (correctYourUsername && rawPlayer1 && rawPlayer2) {
    // Determine which player is actually "you"
    if (correctYourUsername === rawPlayer1) {
      yourUsername = rawPlayer1
      opponentUsername = rawPlayer2
    } else if (correctYourUsername === rawPlayer2) {
      yourUsername = rawPlayer2
      opponentUsername = rawPlayer1
    }
    // If the received yourUsername doesn't match correctYourUsername, log warning (DEV only)
    if (raw?.yourUsername && raw.yourUsername !== correctYourUsername) {
      if ((import.meta as any).env?.DEV) {
        console.warn('[normalizeState] Perspective mismatch detected! Correcting:', {
          received: raw.yourUsername,
          correct: correctYourUsername,
          correctedTo: yourUsername
        })
      }
    }
  }
  
  // Fallback logic for when correctYourUsername is not set
  if (!opponentUsername && !correctYourUsername) {
    if (yourUsername && rawPlayer1 && rawPlayer2) {
      opponentUsername = yourUsername === rawPlayer1 ? rawPlayer2 : rawPlayer1
    } else if (rawPlayer2) {
      opponentUsername = rawPlayer2
    } else if (rawPlayer1 && yourUsername !== rawPlayer1) {
      // Only use rawPlayer1 as fallback if it's not the current user
      opponentUsername = rawPlayer1
    } else {
      // For tournament matches where player2 hasn't joined, show waiting message
      opponentUsername = 'Waiting for opponent...'
    }
  }

  // DEV-only: verbose state normalization logging
  if ((import.meta as any).env?.DEV) {
    console.log('[normalizeState] Input/Output:', {
      input: {
        yourUsername: raw?.yourUsername,
        opponentUsername: raw?.opponentUsername,
        player1Username: raw?.player1Username,
        player2Username: raw?.player2Username,
      },
      correctYourUsername,
      output: {
        yourUsername,
        opponentUsername,
        rawPlayer1,
        rawPlayer2,
      }
    })
  }

  // Clear grace period countdown if game has started (prize drawn) or opponent has joined
  if (prize !== null || (opponentUsername && opponentUsername !== 'Waiting for opponent...' && opponentUsername !== 'Opponent')) {
    try {
      sessionStorage.removeItem('tournament_grace_period_ends_at')
    } catch {}
  }

  return {
    round,
    pot,
    prize,
    scores,
    yourRemaining,
    lastReveal,
    finished,
    decisionDeadline: deadline,
    decisionSeconds: seconds,
    winner,
    endReason,
    matchType: raw?.matchType || 'pve',
    opponentName: opponentUsername || 'Lump',
    opponentUsername: opponentUsername || undefined,
    yourUsername: yourUsername || undefined,
    player1Username: rawPlayer1 || undefined,
    player2Username: rawPlayer2 || undefined,
    you_play: raw?.you_play ?? null,
    opp_play: raw?.opp_play ?? null,
    ui_phase: raw?.ui_phase || 'legacy',
  }
}

export function useRoom(baseURL: string) {
  const [id, setId] = useState<string | null>(null)
  const [state, setState] = useState<RoomState | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  // simple diagnostic sequence counter for WS messages
  const wsSeqRef = useRef<number>(0)
  // Preserve the correct yourUsername to prevent perspective corruption from bad WebSocket auth
  const correctYourUsernameRef = useRef<string | null>(null)

  type TimerHandle = ReturnType<typeof setTimeout>

  const pollingRef = useRef<TimerHandle | null>(null)
  const timeoutCheckRef = useRef<TimerHandle | null>(null)

  // Helper to get headers with JWT token for guests
  const getHeaders = async (): Promise<HeadersInit> => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    
    // Check for regular user JWT token first (all platforms - JWT is primary)
    let regularUserToken: string | null = null;
    if (typeof window !== 'undefined') {
      regularUserToken = localStorage.getItem('regular_user_jwt');
    }
    
    // Check for guest token as fallback
    try {
      const { getValidGuestToken } = await import('./guest-auth');
      const guestToken = await getValidGuestToken();
      // Prefer regular user token over guest token
      if (regularUserToken) {
        headers['Authorization'] = `Bearer ${regularUserToken}`;
      } else if (guestToken) {
        headers['Authorization'] = `Bearer ${guestToken}`;
      }
    } catch {
      // If guest-auth module fails, still use regular user token if available
      if (regularUserToken) {
        headers['Authorization'] = `Bearer ${regularUserToken}`;
      }
    }
    return headers;
  }

  const buildWsUrl = async (gameId: string): Promise<string> => {
    const baseWs = `${baseURL.replace(/^http/, 'ws')}/ws/${gameId}`
    
    // Check for regular user JWT token first (same priority as getHeaders/REST API)
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('regular_user_jwt');
    }
    
    // Fall back to guest token if no regular user token
    if (!token) {
      try {
        const { getValidGuestToken } = await import('./guest-auth')
        const guestToken = await getValidGuestToken()
        if (guestToken) {
          token = guestToken;
        }
      } catch {
        // Ignore token errors; fall back to base URL
      }
    }
    
    // Add token to WebSocket URL if available
    if (token) {
      const sep = baseWs.includes('?') ? '&' : '?'
      return `${baseWs}${sep}token=${encodeURIComponent(token)}`
    }
    
    // No token - rely on session cookies (web browsers only)
    return baseWs
  }

  // Helper to fetch with automatic retry on 401 (for guest token rotation)
  const fetchWithRetry = async (url: string, opts: RequestInit & { retryOn401?: boolean } = {}): Promise<Response> => {
    const { retryOn401 = true, ...fetchOpts } = opts;

    // Get initial token
    let headers = await getHeaders();
    if (fetchOpts.headers) {
      headers = { ...headers, ...fetchOpts.headers };
    }

    const res = await fetch(url, {
      ...fetchOpts,
      credentials: 'include',
      headers,
    });

    // If 401 and retry enabled, try refreshing token and retry once
    if (!res.ok && res.status === 401 && retryOn401) {
      try {
        const { getValidGuestToken } = await import('./guest-auth');
        const freshToken = await getValidGuestToken();
        if (freshToken) {
          const retryHeaders: HeadersInit = { 'Content-Type': 'application/json' };
          if (fetchOpts.headers) {
            Object.assign(retryHeaders, fetchOpts.headers);
          }
          retryHeaders['Authorization'] = `Bearer ${freshToken}`;

          const retryRes = await fetch(url, {
            ...fetchOpts,
            credentials: 'include',
            headers: retryHeaders,
          });
          return retryRes;
        }
      } catch (err) {
        console.error('[useRoom] Token refresh failed during retry:', err);
        // If refresh fails, return original response
      }
    }

    return res;
  }

  // Polling fallback for timeout detection and game state updates
  const startPolling = (gameId: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
    }

    let lastPrizeState: number | null = null
    let lastOpponentUsername: string | null = null

    pollingRef.current = setInterval(async () => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${baseURL}/game/${gameId}/state`, {
          headers,
          credentials: 'include'
        })
        if (response.ok) {
          const gameState = await response.json()
          const normalized = normalizeState(gameState, correctYourUsernameRef.current)

          // Update state if game started (prize drawn) - this handles grace period expiration
          // Check if prize state changed from null to a number (game started)
          if (normalized.prize !== null && lastPrizeState === null) {
            setState(normalized)
            lastPrizeState = normalized.prize
            lastOpponentUsername = normalized.opponentUsername || null
          }
          // Also update if game finished (for any reason - timeout, tournament completion, etc.)
          else if (normalized.finished) {
            setState(normalized)
            clearInterval(pollingRef.current!)
            pollingRef.current = null
          }
          // Update if opponent joined (opponentUsername changed from "Waiting for opponent..." to actual name)
          else if (normalized.opponentUsername && normalized.opponentUsername !== "Waiting for opponent..." && 
                   (lastOpponentUsername === null || lastOpponentUsername === "Waiting for opponent...") &&
                   normalized.opponentUsername !== lastOpponentUsername) {
            setState(normalized)
            lastOpponentUsername = normalized.opponentUsername
          }
          // Update lastPrizeState to track changes
          else if (normalized.prize !== lastPrizeState) {
            lastPrizeState = normalized.prize
            // If prize changed (new round), update state
            setState(normalized)
          }
          // Always update lastOpponentUsername to track changes
          if (normalized.opponentUsername !== lastOpponentUsername) {
            lastOpponentUsername = normalized.opponentUsername || null
          }
        }
      } catch (err) {
        console.error('Polling failed:', err)
      }
    }, 1000) // Poll every second
  }

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  const startTimeoutCheck = (gameId: string, deadline: number) => {
    if (timeoutCheckRef.current) {
      clearTimeout(timeoutCheckRef.current)
    }

    const timeoutMs = deadline - Date.now()
    if (timeoutMs > 0) {
      timeoutCheckRef.current = setTimeout(async () => {
        try {
          const headers = await getHeaders();
          const response = await fetch(`${baseURL}/game/${gameId}/state`, {
            headers,
            credentials: 'include'
          })
          if (response.ok) {
            const gameState = await response.json()
            const normalized = normalizeState(gameState, correctYourUsernameRef.current)
            if (normalized.finished && normalized.endReason?.startsWith('timeout')) {
              setState(normalized)
            }
          }
        } catch (err) {
          console.error('Client timeout check failed:', err)
        }
      }, timeoutMs + 1000) // Check 1 second after deadline
    }
  }

  const stopTimeoutCheck = () => {
    if (timeoutCheckRef.current) {
      clearTimeout(timeoutCheckRef.current)
      timeoutCheckRef.current = null
    }
  }

  async function newGame(wager?: number) {
    const body = wager ? { wager } : {}
    const r = await fetchWithRetry(`${baseURL}/game/new`, {
      method: 'POST',
      body: JSON.stringify(body),
      retryOn401: true,
    })
    if (!r.ok) {
      const errorText = await r.text()
      throw new Error(`newGame failed: ${r.status} - ${errorText}`)
    }

    const j = await r.json()
    setId(j.id)
    const normalizedState = normalizeState(j.state)
    // Store the correct yourUsername from the initial REST API response (properly authenticated)
    if (normalizedState.yourUsername) {
      correctYourUsernameRef.current = normalizedState.yourUsername
    }
    setState(normalizedState)

    // Start polling as fallback for timeout detection
    startPolling(j.id)

    // Start client-side timeout check if there's a deadline
    if (normalizedState.decisionDeadline) {
      startTimeoutCheck(j.id, normalizedState.decisionDeadline)
    }

    // Open WS for live state pushes with reconnection logic
    const connectWebSocket = () => {
      buildWsUrl(j.id)
        .then((wsURL) => {
          const ws = new WebSocket(wsURL)

          ws.onopen = () => {
            // WebSocket connected
          }

          ws.onclose = (event) => {
            // Don't reconnect if it's a normal closure or if game is finished
            if (event.code !== 1000 && !state?.finished) {
              setTimeout(connectWebSocket, 2500)
            }
          }

          ws.onerror = (error) => {
            console.error(`[WS] WebSocket error:`, error)
          }

          ws.onmessage = (ev) => {
            try {
              const msg = JSON.parse(ev.data)
              // server usually sends { t:"state", roomId, ...publicState }
              if (msg?.t === 'state') {
                const next = normalizeState(msg, correctYourUsernameRef.current)

                // Additional timeout debugging
                if (next.finished && next.endReason?.startsWith('timeout')) {
                  // Stop polling since we got the state via WebSocket
                  stopPolling()
                }

                setState(next)
              } else {
                // fallback: some servers push the bare state
                setState(normalizeState(msg, correctYourUsernameRef.current))
              }
            } catch {
              /* ignore parse errors */
            }
          }
          wsRef.current = ws
        })
        .catch((error) => {
          console.error(`[WS] Failed to connect:`, error)
        })
    }

    connectWebSocket()
  }

  // Create a game after matchmaking (PvP or PvE)
  async function newMatchedGame(params: { matchType: 'pvp' | 'pve'; wager: number; opponentId?: number | string; opponentUsername?: string; gid?: string; tournamentMatchId?: number }) {
    const body: any = {
      match_type: params.matchType,
      wager: params.wager,
    }
    if (params.opponentId) body.opponent_id = params.opponentId  // Can be number (PvP) or string (PvE registry key)
    if (params.opponentUsername) body.opponent_username = params.opponentUsername
    if (params.gid) body.gid = params.gid
    if (params.tournamentMatchId) {
      body.tournament_match_id = params.tournamentMatchId
      if ((import.meta as any).env?.DEV) {
        console.log(`[TOURNAMENT] Creating tournament game. tournamentMatchId: ${params.tournamentMatchId}, body:`, JSON.stringify(body, null, 2))
      }
    }

    const r = await fetchWithRetry(`${baseURL}/matchmaking/create-game`, {
      method: 'POST',
      body: JSON.stringify(body),
      retryOn401: true,
      headers: { 'Content-Type': 'application/json' }, // fetchWithRetry will merge with getHeaders() which includes JWT
    })
    if (!r.ok) {
      const errorText = await r.text()
      if (params.tournamentMatchId) {
        console.error(`[TOURNAMENT] Tournament game creation failed with status ${r.status}:`, errorText)
        console.error(`[TOURNAMENT] Request body was:`, JSON.stringify(body, null, 2))
      } else {
        console.error(`[Matchmaking] create-game failed: ${r.status} - ${errorText}`)
      }
      const error: any = new Error(`newMatchedGame failed: ${r.status} - ${errorText}`)
      error.status = r.status
      error.details = errorText
      throw error
    }
    const j = await r.json()
    setId(j.id)
    const normalizedState = normalizeState(j.state)
    // Store the correct yourUsername from the initial REST API response (properly authenticated)
    if (normalizedState.yourUsername) {
      correctYourUsernameRef.current = normalizedState.yourUsername
    }
    setState(normalizedState)

    // Clear tournament match starting flag once game is created
    if (params.tournamentMatchId) {
      try {
        sessionStorage.removeItem('tournament_match_starting');
      } catch {}
      // Store grace period if provided in response
      if (j.grace_period_ends_at) {
        sessionStorage.setItem('tournament_grace_period_ends_at', j.grace_period_ends_at);
        if ((import.meta as any).env?.DEV) {
          console.log('[TOURNAMENT] Grace period from create-game response:', j.grace_period_ends_at);
        }
      }
    }

    // Get opponent name from response
    const opponentName = j.opponent_username || normalizedState.opponentName || normalizedState.opponentUsername || 'Lump'

    // Refresh account after game creation (gear was consumed)
    // Dispatch event to update account in all components
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('accountNeedsRefresh'));
    }

    // Start polling as fallback for timeout detection
    startPolling(j.id)

    // Start client-side timeout check if there's a deadline
    if (normalizedState.decisionDeadline) {
      startTimeoutCheck(j.id, normalizedState.decisionDeadline)
    }

    // Open WS for live state pushes
    buildWsUrl(j.id)
      .then((wsURL) => {
        const ws = new WebSocket(wsURL)
        ws.onerror = (error) => {
          console.error(`[WS] WebSocket error:`, error)
        }
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data)
            if (msg?.t === 'state') setState(normalizeState(msg, correctYourUsernameRef.current))
            else setState(normalizeState(msg, correctYourUsernameRef.current))
          } catch {}
        }
        wsRef.current = ws
      })
      .catch((error) => {
        console.error(`[WS] Failed to connect:`, error)
      })

    // Return opponent name for display (was returning j, but we need opponent name)
    return opponentName
  }

  async function play(nugget: number) {
    if (!id) return
    await fetchWithRetry(`${baseURL}/game/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ nugget }),
      retryOn401: true,
    })
    // state normally arrives via WS; if no WS, you could poll /state here
  }

  async function nextRound() {
    if (!id) return
    // Ask backend to draw ONLY the next prize (used after reveal delay)
    await fetchWithRetry(`${baseURL}/game/${id}/next`, {
      method: 'POST',
      retryOn401: true,
    })
  }

  async function completeGame(data: {
    outcome: 'you' | 'opp' | 'tie'
    yourScore?: number
    opponentScore?: number
    opponentUsername?: string
    endReason?: string | null
  }) {
    if (!id) {
      console.error('Cannot complete game: no game ID');
      return;
    }
    if (!state) {
      console.error('Cannot complete game: no game state');
      return;
    }

    // Extract scores from state if not provided
    const yourScore = data.yourScore ?? state.scores.you
    const opponentScore = data.opponentScore ?? state.scores.opp
    const opponentUsername = data.opponentUsername ?? state.opponentUsername ?? state.opponentName ?? 'Lump'

    try {

      const r = await fetchWithRetry(`${baseURL}/game/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          outcome: data.outcome,
          yourScore,
          opponentScore,
          opponentUsername,
          endReason: data.endReason ?? state.endReason ?? null,
        }),
        retryOn401: true,
      })
      if (r.ok) {
        const result = await r.json()
        return result
      } else {
        console.error('Game completion failed:', r.status, await r.text());
      }
    } catch (err) {
      console.error('Failed to complete game:', err)
    }
  }

  // Remove automatic newGame() call - now called manually when user selects wager
  // useEffect(() => {
  //   newGame()
  //   return () => {
  //     try { wsRef.current?.close() } catch {}
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  function resetGame() {
    setId(null);
    setState(null);
    correctYourUsernameRef.current = null; // Clear preserved username on reset
    stopPolling();
    stopTimeoutCheck();
    try {
      wsRef.current?.close(1000, 'Game reset')
    } catch {}
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling()
      stopTimeoutCheck()
      try { wsRef.current?.close(1000, 'Component unmount') } catch {}
    }
  }, [])

  return { id, setId, state, play, newGame, newMatchedGame, nextRound, completeGame, resetGame }
}
